import Select from "react-select";
import React, { useState } from "react";

interface FilterValues {
  name: string[];
  status: string[];
  fromDate: string;
  toDate: string;
}

interface FilterButtonProps {
  onApply: (filters: FilterValues) => void;
  statusList: { value: string; label: string }[];
  userList: { value: string; label: string }[];
  showName?: boolean;
  showStatus?: boolean;
  showDateRange?: boolean;
}

const FilterButton: React.FC<FilterButtonProps> = ({
  onApply,
  statusList,
  userList,
  showName = true,
  showStatus = true,
  showDateRange = true,
}) => {
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState<FilterValues>({
    name: [],
    status: [],
    fromDate: "",
    toDate: "",
  });
  const [appliedFilters, setAppliedFilters] = useState<FilterValues>({
    name: [],
    status: [],
    fromDate: "",
    toDate: "",
  });

  // Check if any applied filter is active
  const isFiltered =
    (appliedFilters.name && appliedFilters.name.length > 0) ||
    (appliedFilters.status && appliedFilters.status.length > 0) ||
    appliedFilters.fromDate !== "" ||
    appliedFilters.toDate !== "";

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleApply = () => {
    onApply(filters);
    setAppliedFilters(filters);
    setOpen(false);
  };

  const handleClear = () => {
    setFilters({ name: [], status: [], fromDate: "", toDate: "" });
    setAppliedFilters({ name: [], status: [], fromDate: "", toDate: "" });
    onApply({ name: [], status: [], fromDate: "", toDate: "" });
    setOpen(false);
  };

  return (
    <div className="relative inline-block">
      <button
        className="relative inline-flex items-center py-2.5 px-5 me-2 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700"
        onClick={() => setOpen((prev) => !prev)}
        type="button"
      >
        Filter
        {isFiltered && (
          <span className="absolute top-0 right-0 w-2 h-2 bg-blue-500 rounded-full"></span>
        )}
      </button>
      {open && (
        <div className="absolute z-10 bg-white border rounded shadow-md p-4 mt-2 min-w-[250px]">
          {showName && (
            <div className="mb-4">
              <label className="block text-xs mb-1">Name</label>

              <Select
                options={userList}
                isMulti
                placeholder="Select Names"
                onChange={(selected) => {
                  const values = selected.map((item) => item.value);
                  setFilters((prev) => ({ ...prev, name: values }));
                }}
              />
            </div>
          )}
          {showStatus && (
            <div className="mb-4">
              <label className="block text-xs mb-1">Status</label>
              <Select
                options={statusList}
                isMulti
                placeholder="Select Status"
                onChange={(selected) => {
                  const values = selected.map((item) => item.value);
                  setFilters((prev) => ({ ...prev, status: values }));
                }}
              />
            </div>
          )}
          {showDateRange && (
            <div className="mt-4 flex gap-2">
              <div>
                <label className="block text-xs mb-1">From</label>
                <input
                  type="date"
                  name="fromDate"
                  value={filters.fromDate}
                  onChange={handleChange}
                  className="border rounded px-2 py-1"
                />
              </div>
              <div>
                <label className="block text-xs mb-1">To</label>
                <input
                  type="date"
                  name="toDate"
                  value={filters.toDate}
                  onChange={handleChange}
                  className="border rounded px-2 py-1"
                  min={filters.fromDate}
                />
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <button
              className="px-3 py-1 rounded bg-blue-600 text-white"
              onClick={handleApply}
              type="button"
            >
              Apply
            </button>
            <button
              className="px-3 py-1 rounded bg-gray-200"
              onClick={() => setOpen(false)}
              type="button"
            >
              Close
            </button>
            <button
              className={`px-3 py-1 rounded transition-colors duration-150 ${isFiltered ? "bg-blue-600 text-white hover:bg-blue-700" : "bg-gray-200 text-gray-400 cursor-not-allowed"}`}
              onClick={handleClear}
              type="button"
              disabled={!isFiltered}
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterButton;
